import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtNum, todayISO, fmtDate } from "../lib/api";
import { openSupplierStatementWindow } from "../lib/bill";
import { Wallet, Truck, IndianRupee, Trash2, Printer } from "lucide-react";
import { toast } from "sonner";

export default function Payables() {
  const [report, setReport] = useState(null);
  const [selected, setSelected] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMode, setPayMode] = useState("cash");
  const [payNotes, setPayNotes] = useState("");

  const load = async () => {
    const [res, supRes] = await Promise.all([
      api.get("/reports/payables"),
      api.get("/suppliers"),
    ]);
    const rep = res.data;
    const names = new Set(rep.suppliers.map((s) => s.supplier));
    supRes.data.forEach((s) => {
      if (!names.has(s.name)) {
        rep.suppliers.push({
          supplier: s.name,
          total_billed: 0,
          total_paid_on_invoice: 0,
          extra_paid: 0,
          outstanding: 0,
          purchases: [],
        });
      }
    });
    setReport(rep);
  };

  useEffect(() => {
    load();
  }, []);

  const openSupplier = async (s) => {
    setSelected(s);
    const res = await api.get(`/reports/supplier-ledger?supplier=${encodeURIComponent(s)}`);
    setLedger(res.data);
  };

  const recordPayment = async () => {
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    await api.post("/supplier-payments", {
      supplier: selected,
      date: todayISO(),
      amount: amt,
      mode: payMode,
      notes: payNotes,
    });
    toast.success(`Recorded ${fmtINR(amt)} payment to ${selected}`);
    setPayAmount("");
    setPayNotes("");
    await openSupplier(selected);
    load();
  };

  const deleteSupplierPayment = async (id) => {
    if (!window.confirm("Delete this payment?")) return;
    await api.delete(`/supplier-payments/${id}`);
    await openSupplier(selected);
    load();
  };

  if (!report) {
    return (
      <div className="p-8 text-zinc-500" data-testid="payables-loading">
        Loading…
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto page-enter" data-testid="payables-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
          <Wallet className="h-3 w-3" /> What You Owe
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
          Accounts Payable
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="border-2 border-rose-200 bg-rose-50 p-5" data-testid="payables-grand-total">
          <div className="text-[10px] uppercase tracking-wider text-rose-700">Grand Outstanding</div>
          <div className="font-display text-3xl font-bold font-mono-num text-rose-700 mt-1">
            {fmtINR(report.grand_outstanding)}
          </div>
        </div>
        <div className="border border-zinc-200 bg-white p-5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Suppliers Pending</div>
          <div className="font-display text-3xl font-bold font-mono-num mt-1">
            {report.suppliers.filter((s) => s.outstanding > 0).length}
          </div>
        </div>
        <div className="border border-zinc-200 bg-white p-5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Total Suppliers</div>
          <div className="font-display text-3xl font-bold font-mono-num mt-1">
            {report.suppliers.length}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
            Suppliers
          </div>
          <div className="border border-zinc-200 bg-white" data-testid="payables-supplier-list">
            {report.suppliers.length === 0 ? (
              <div className="p-5 text-sm text-zinc-500 text-center">
                No purchases on credit yet 🎉
              </div>
            ) : (
              report.suppliers.map((s) => (
                <button
                  key={s.supplier}
                  onClick={() => openSupplier(s.supplier)}
                  data-testid={`supplier-row-${s.supplier}`}
                  className={`w-full text-left border-b border-zinc-200 px-4 py-3 hover:bg-zinc-50 ${
                    selected === s.supplier ? "bg-sky-50" : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Truck className="h-5 w-5 text-zinc-400 shrink-0" />
                      <div className="text-sm font-semibold truncate">{s.supplier}</div>
                    </div>
                    <div className={`font-mono-num font-bold text-sm ${s.outstanding > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                      {fmtINR(s.outstanding)}
                    </div>
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 pl-7">
                    {s.purchases.length} purchases · billed {fmtINR(s.total_billed)}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          {!ledger ? (
            <div className="border border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500">
              Select a supplier to view ledger and record payments
            </div>
          ) : (
            <div data-testid="supplier-detail">
              <div className="border border-zinc-200 bg-white p-5 mb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Supplier</div>
                    <div className="font-display text-2xl font-bold">{ledger.supplier}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Outstanding</div>
                    <div className={`font-display text-2xl font-bold font-mono-num ${ledger.outstanding > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                      {fmtINR(ledger.outstanding)}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-zinc-200">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Billed</div>
                    <div className="font-mono-num font-semibold">{fmtINR(ledger.total_billed)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Paid on Invoice</div>
                    <div className="font-mono-num font-semibold">{fmtINR(ledger.total_paid_on_invoice)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Extra Paid</div>
                    <div className="font-mono-num font-semibold">{fmtINR(ledger.extra_paid)}</div>
                  </div>
                </div>
                {/* Statement / receipt generation */}
                <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-zinc-200">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                    <Printer className="h-3 w-3" /> Statement
                  </span>
                  {[
                    { k: "all", label: "Consolidated" },
                    { k: "chicken", label: "Chicken Only" },
                    { k: "mutton", label: "Mutton Only" },
                  ].map((c) => (
                    <button
                      key={c.k}
                      onClick={() => openSupplierStatementWindow(ledger, c.k)}
                      data-testid={`supplier-receipt-${c.k}`}
                      className="border border-zinc-900 px-3 py-1.5 text-[10px] uppercase tracking-wider hover:bg-zinc-900 hover:text-white transition-colors"
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Record payment */}
              <div className="border border-zinc-200 bg-white p-5 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <IndianRupee className="h-4 w-4 text-emerald-600" />
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                    Pay Supplier
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                  <input
                    type="number"
                    step="1"
                    placeholder="Amount ₹"
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                    data-testid="supplier-pay-amount"
                    className="md:col-span-3 border border-zinc-300 px-3 py-2 text-sm font-mono-num focus:outline-none focus:ring-2 focus:ring-sky-600"
                  />
                  <select
                    value={payMode}
                    onChange={(e) => setPayMode(e.target.value)}
                    data-testid="supplier-pay-mode"
                    className="md:col-span-3 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                  >
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                  </select>
                  <input
                    placeholder="Notes (optional)"
                    value={payNotes}
                    onChange={(e) => setPayNotes(e.target.value)}
                    data-testid="supplier-pay-notes"
                    className="md:col-span-4 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                  />
                  <button
                    onClick={recordPayment}
                    data-testid="supplier-pay-save"
                    className="md:col-span-2 bg-emerald-600 text-white px-3 py-2 text-xs uppercase tracking-wider hover:bg-emerald-700"
                  >
                    Pay
                  </button>
                </div>

                {ledger.payments.length > 0 && (
                  <div className="border-t border-zinc-200 mt-3 pt-3">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                      Payment History
                    </div>
                    <table className="w-full text-xs">
                      <tbody>
                        {ledger.payments.map((p) => (
                          <tr key={p.id} className="border-b border-zinc-100" data-testid={`supplier-payment-${p.id}`}>
                            <td className="py-1.5">{fmtDate(p.date)}</td>
                            <td className="py-1.5 uppercase text-zinc-500">{p.mode}</td>
                            <td className="py-1.5">{p.notes || "-"}</td>
                            <td className="py-1.5 font-mono-num text-right font-semibold text-emerald-700">
                              {fmtINR(p.amount)}
                            </td>
                            <td className="py-1.5 text-right">
                              <button
                                onClick={() => deleteSupplierPayment(p.id)}
                                className="text-zinc-300 hover:text-rose-600"
                                data-testid={`supplier-payment-delete-${p.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Purchase list */}
              <div className="border border-zinc-200 bg-white overflow-x-auto">
                <div className="px-5 py-3 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Purchases
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500">
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Type</th>
                      <th className="px-4 py-2 text-right">Kg</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                      <th className="px-4 py-2 text-right">Paid</th>
                      <th className="px-4 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.purchases.map((p) => (
                      <tr key={p.id} className="border-b border-zinc-200" data-testid={`supplier-purchase-${p.id}`}>
                        <td className="px-4 py-2 text-xs">{fmtDate(p.date)}</td>
                        <td className="px-4 py-2 text-xs capitalize">{p.type.replace(/_/g, " ")}</td>
                        <td className="px-4 py-2 text-xs font-mono-num text-right">{fmtNum(p.total_kg, 2)}</td>
                        <td className="px-4 py-2 text-xs font-mono-num text-right font-semibold">{fmtINR(p.total_amount)}</td>
                        <td className="px-4 py-2 text-xs font-mono-num text-right">{fmtINR(p.paid_amount)}</td>
                        <td className="px-4 py-2 text-xs text-center">
                          <span
                            className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${
                              p.payment_status === "paid"
                                ? "bg-emerald-50 text-emerald-700"
                                : p.payment_status === "partial"
                                ? "bg-amber-50 text-amber-700"
                                : "bg-rose-50 text-rose-700"
                            }`}
                          >
                            {p.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
